import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, XCircle, Spinner, Crown } from "@phosphor-icons/react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { setUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          confetti({ particleCount: 160, spread: 90, origin: { y: 0.6 }, colors: ["#FF007F", "#00FFFF", "#FFD600", "#39FF14"] });
          api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
          return;
        }
        if (data.status === "expired") { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, setUser]);

  const isLicense = info?.purpose === "license";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center border border-zinc-800 bg-[#18181B] p-10">
        {status === "checking" && (<>
          <Spinner size={56} className="mx-auto animate-spin" color="#00FFFF" />
          <h1 className="font-display text-2xl font-black uppercase mt-4">Confirming payment…</h1>
          <p className="text-zinc-400 mt-2">Hang tight, this only takes a moment.</p>
        </>)}
        {status === "success" && isLicense && (<>
          <Crown size={64} weight="fill" color="#FFD600" className="mx-auto" />
          <h1 className="font-display text-2xl font-black uppercase mt-4">Your Platform Is Live! 👑</h1>
          <p className="text-zinc-400 mt-2"><b>{info?.brand_name}</b> is ready. Open your dashboard to add your deals and set prices.</p>
          <div className="flex gap-3 justify-center mt-6 flex-wrap">
            <Link to="/dashboard" className="px-5 py-3 bg-[#FFD600] text-black font-bold uppercase tracking-widest text-sm border border-[#FFD600] hover:bg-transparent hover:text-[#FFD600] transition-colors">My Dashboard</Link>
            {info?.slug && <Link to={`/site/${info.slug}`} className="px-5 py-3 border border-zinc-600 font-bold uppercase tracking-widest text-sm hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors">View My Store</Link>}
          </div>
        </>)}
        {status === "success" && !isLicense && (<>
          <CheckCircle size={64} weight="fill" color="#39FF14" className="mx-auto" />
          <h1 className="font-display text-2xl font-black uppercase mt-4">You're Live! 🎉</h1>
          <p className="text-zinc-400 mt-2">Your <b>{info?.tier_name}</b> listing is now published. A receipt is on its way to your email.</p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/my-links" className="px-5 py-3 bg-[#FF007F] text-white font-bold uppercase tracking-widest text-sm border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">My Links</Link>
            <Link to="/" className="px-5 py-3 border border-zinc-600 font-bold uppercase tracking-widest text-sm hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors">Home</Link>
          </div>
        </>)}
        {(status === "failed" || status === "timeout") && (<>
          <XCircle size={64} weight="fill" color="#FF007F" className="mx-auto" />
          <h1 className="font-display text-2xl font-black uppercase mt-4">{status === "timeout" ? "Still Processing" : "Payment Not Completed"}</h1>
          <p className="text-zinc-400 mt-2">{status === "timeout" ? "Give it a minute and check your dashboard / listings." : "No worries, you can try again anytime."}</p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/" className="px-5 py-3 bg-[#FF007F] text-white font-bold uppercase tracking-widest text-sm border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">Home</Link>
          </div>
        </>)}
      </div>
    </div>
  );
}

